import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';

import { MyBetsConfig, PrettyUrlsConfig, SitecoreMyBetsConfig } from '@frontend/sports/common/client-config-data-access';
import { ofType } from '@frontend/sports/common/core/utils/extended-types';
import { CustomLoadingIndicatorService } from '@frontend/sports/loading-indicator-feature';
import { MyBetsUrlParam } from '@frontend/sports/navigation/core/feature/url-helper';
import { BetslipListType } from '@frontend/sports/types/models/my-bets';
import { SessionStoreService } from '@frontend/vanilla/core';
import { Observable, firstValueFrom, from, of, tap } from 'rxjs';

import { StylesLazyLoadingService } from '../deferred/styles/style-loading.service';
import { IBetSlipRepositoryResult, MigrationTabType } from '../my-bets-base/models/my-bets-viewmodels';
import { MyBetsDateRangeFilterContstraint, MyBetsFilterConstraint, MyBetsStateFilterContstraint } from './models/my-bets-filter-constraints';
import { AssociatedAccountService } from './services/associated-account-service';
import { MyBetsBetslipRepository } from './services/my-bets-betslip.repository';

interface StateConstraint {
    state: string;
    title: string;
}

interface DateConstraint {
    fromDate: string;
    toDate: string;
    title: string;
}

@Injectable()
export class MyBetsResolver {
    repositoryResult: IBetSlipRepositoryResult;
    private stateFilterConstraintSessionStorageKey = 'stateConstraint';
    private dateRangeFilterConstraintSessionStorageKey = 'dateRangeConstraint';
    private lastSelectedMigrationTabStorageKey = 'lastSelectedMigrationTab';
    filterConstraint: MyBetsFilterConstraint = new MyBetsFilterConstraint();

    private routeTags = new Map<string, BetslipListType>();
    private stateConstraint: MyBetsStateFilterContstraint;
    private rangeConstraint: MyBetsDateRangeFilterContstraint;
    private isSettledTabInitialLoad = true;
    private previousUrl = '';

    constructor(
        private repository: MyBetsBetslipRepository,
        private mybetsConfig: MyBetsConfig,
        private sessionStorage: SessionStoreService,
        private urlConfig: PrettyUrlsConfig,
        private router: Router,
        private associatedAccountService: AssociatedAccountService,
        private stylesLazyLoadingService: StylesLazyLoadingService,
        private sitecoreMyBets: SitecoreMyBetsConfig,
        private loadingIndicator: CustomLoadingIndicatorService,
    ) {
        this.routeTags.set(this.urlConfig.translations.live, BetslipListType.Live);
        this.routeTags.set(this.urlConfig.translations.open, BetslipListType.Open);
        this.routeTags.set(this.urlConfig.translations.settled, BetslipListType.Settled);

        this.router.events.pipe(ofType(NavigationEnd), takeUntilDestroyed()).subscribe((event) => {
            this.previousUrl = event.url;
        });
    }

    /**
     * This is a non-blocking resolver, it returns a promise of an observable. The navigation is blocked until it's
     * outer promise is complete, but does not wait for the observer to emit.
     */
    async resolve(routeSnapshot: ActivatedRouteSnapshot): Promise<Observable<IBetSlipRepositoryResult | null>> {
        if (!routeSnapshot?.params.tab) {
            return of(null);
        }

        const handler = this.loadingIndicator.start('MY_BETS_LOADING');
        this.handleFilterConstraint(routeSnapshot.params.tab);

        const betSlipsPromise = this.repository.getBetSlips({
            pageSize: this.mybetsConfig.pageSize,
            filterConstraint: this.filterConstraint,
            listType: this.routeTags.get(routeSnapshot.params.tab) as BetslipListType,
        });

        return from(
            Promise.all([betSlipsPromise, this.stylesLazyLoadingService.lazyLoad('my-bets'), firstValueFrom(this.sitecoreMyBets.whenReady)]).then(
                ([betSlips]) => betSlips,
            ),
        ).pipe(tap(() => handler.done()));
    }

    private handleFilterConstraint(routeTag: string): void {
        this.filterConstraint.clean();

        const stateConstraint = this.sessionStorage.get<StateConstraint>(this.stateFilterConstraintSessionStorageKey);
        const dateConstraint = this.sessionStorage.get<DateConstraint>(this.dateRangeFilterConstraintSessionStorageKey);
        const associatedAccount = this.associatedAccountService.getAssociatedAccount();

        const isSettled = routeTag === MyBetsUrlParam.Settled;

        if (associatedAccount) {
            const lastSelectedMigrationTab = this.sessionStorage.get<MigrationTabType>(this.lastSelectedMigrationTabStorageKey);

            if (lastSelectedMigrationTab && lastSelectedMigrationTab === MigrationTabType.Before) {
                this.filterConstraint.addConstraint(this.repository.getBeforeFilter(associatedAccount, false, isSettled));
            }
        }

        if (isSettled && !this.isSettledTabInitialLoad) {
            if (stateConstraint) {
                this.stateConstraint = new MyBetsStateFilterContstraint(stateConstraint.title, stateConstraint.state);
                this.filterConstraint.addConstraint(this.stateConstraint);
            }
            if (dateConstraint) {
                this.rangeConstraint = new MyBetsDateRangeFilterContstraint(
                    dateConstraint.title,
                    new Date(dateConstraint.fromDate),
                    new Date(dateConstraint.toDate),
                );
                this.filterConstraint.addConstraint(this.rangeConstraint);
            }
        }

        if (isSettled && this.isSettledTabInitialLoad) {
            this.isSettledTabInitialLoad = false;
        } else if (this.previousUrl.indexOf(this.urlConfig.translations.myBets) < 0) {
            this.isSettledTabInitialLoad = true;
        }
    }
}
